import { useMutation } from '@apollo/client'
import { faLock, faLockAlt } from '@fortawesome/pro-regular-svg-icons'
import { Formik, FormikHelpers as FormikActions } from 'formik'
import gql from 'graphql-tag'
import React from 'react'
import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import * as Yup from 'yup'
import { login, updateSession } from 'session'
import { Content, Form, Input } from 'ui'
import { RequiredString } from 'validations'

/* BEGIN - GQL Queries / Mutations */
/**
 * This mutation will update the user's password upon
 * form submission.
 */
const RESET_PASSWORD_MUTATION = gql`
  mutation ResetPassword($password: String!) {
    response: resetPassword(password: $password) {
      user {
        emailAddress
      }
      errors {
        path
        message
      }
      success
    }
  }
`

type ErrorList = Array<{ path: string; message: string }> | null

/**
 * This interface represents the output from the update
 * profile mutation.
 */
interface IUserMutationData {
  user: {
    emailAddress: string
  }
  success: boolean
  errors: ErrorList
}
/* END - GQL Queries / Mutations */

/* BEGIN - Form Values */
/**
 * This interface represents potential form values and
 * is passed into our custom form field factories to get
 * type safety on our form.
 */
export interface IResetPasswordValues {
  [key: string]: string | undefined
  password?: string | undefined
  passwordConfirmation?: string | undefined
}

/**
 * This schema is used by formik to provide client side
 * form validation.
 */
const ResetSchema = Yup.object().shape({
  password: RequiredString().min(7),
  passwordConfirm: RequiredString()
    .oneOf([Yup.ref('password')], 'must match password')
    .required('Password confirm is required'),
})
/* END - Form Values */

/* BEGIN - Custom Form Renderers */
const FormField = Input.Renderer<IResetPasswordValues>()
/* END - Custom Form Renderers */

interface IProps extends RouteComponentProps {
  dispatch: (action: any) => any
}

/**
 * A simple reset password form for existing users.
 */
const FormView: React.FC<IProps> = (props) => {
  const [resetPassword, { error: mutationError }] = useMutation<
    { response: IUserMutationData },
    { password: string }
  >(RESET_PASSWORD_MUTATION)

  const handleFormSubmit = async (
    values: IResetPasswordValues,
    actions: FormikActions<any>
  ) => {
    const result = await resetPassword({
      variables: {
        password: values.password as string,
      },
    })
    if (
      result.data &&
      !result.data.response.success &&
      result.data.response.errors
    ) {
      result.data.response.errors.forEach((fieldError) =>
        actions.setFieldError(`${fieldError.path}`, fieldError.message)
      )
    } else {
      const auth = {
        email:
          result.data && result.data.response.user
            ? (result.data.response.user.emailAddress as string)
            : '',
        password: values.password as string,
      }
      try {
        const authTokens = await login(
          process.env.REACT_APP_EPC_API_URL ??
            process.env.REACT_APP_API_URL ??
            '',
          auth
        )
        props.dispatch(
          updateSession({ ...authTokens, temporaryAccessToken: '' })
        )
      } catch (e) {
        actions.setFieldError('_', e.message)
        return
      }
      props.dispatch(updateSession({ temporaryAccessToken: '' }))
      props.history.push('/login')
    }
    actions.setSubmitting(false)
  }

  return (
    <Formik
      initialValues={{
        password: '',
        passwordConfirm: '',
      }}
      onSubmit={handleFormSubmit}
      validationSchema={ResetSchema}
    >
      {({ handleSubmit, isSubmitting, isValid, submitForm }) => {
        return (
          <Form.Form onSubmit={handleSubmit}>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={isSubmitting}
                  icon={faLock}
                  name="password"
                  label="Password"
                  type="password"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.Row>
              <Form.Item width={[1]}>
                <FormField
                  disabled={isSubmitting}
                  icon={faLockAlt}
                  name="passwordConfirm"
                  label="Confirm Password"
                  type="password"
                  required={true}
                />
              </Form.Item>
            </Form.Row>
            <Form.GeneralError name="_" />
            <Form.ErrorComponent
              error={mutationError ? mutationError.message : null}
            />
            <Form.Row>
              <Form.Item width={1}>
                <Content.ActionButton
                  disabled={!isValid}
                  label="Reset Password"
                  loading={isSubmitting}
                  onClick={submitForm}
                />
              </Form.Item>
            </Form.Row>
          </Form.Form>
        )
      }}
    </Formik>
  )
}

export const ResetPassword = connect()(withRouter(FormView))
